import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  CheckCircle, Loader2, XCircle, Repeat,
  ArrowRight, ArrowLeft, Sparkles, TrendingDown, Clock,
} from 'lucide-react';

import { API } from '../utils/apiConfig';

/**
 * Format an ISO timestamp to "Mi 15. Mai · 08:00".
 * Uses the user's active locale so it works for all 10 supported languages.
 */
const formatDeparture = (iso, locale = 'de') => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: 'short' });
    const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    return `${date} · ${time}`;
  } catch {
    return iso;
  }
};

export const BookingSuccess = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [bundle, setBundle] = useState(null);

  const sessionId = searchParams.get('session_id');
  // accept both ?bundle= and ?bundle_id= for forward-compat with deeper links
  const bundleQuery = searchParams.get('bundle') || searchParams.get('bundle_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/my-rides');
      return;
    }
    pollPaymentStatus(sessionId);
    if (bundleQuery) fetchBundle(bundleQuery);
    // eslint-disable-next-line
  }, []);

  const fetchBundle = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/bookings/round-trip/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.data?.bundle) setBundle(r.data.bundle);
    } catch (e) {
      console.warn('bundle fetch failed', e?.response?.status);
    }
  };

  const pollPaymentStatus = async (sid, attempt = 0) => {
    if (attempt >= 5) { setStatus('timeout'); return; }
    try {
      const response = await axios.get(`${API}/stripe/checkout-status/${sid}`);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setTimeout(() => navigate('/my-rides'), bundleQuery ? 7000 : 2500);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(attempt + 1);
        setTimeout(() => pollPaymentStatus(sid, attempt + 1), 2000);
      }
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  // ── ABOVE-THE-FOLD Bundle Success (Hin & Rück, no scroll) ─────────────
  const renderBundleSuccess = () => {
    const locale = (i18n.language || 'de').slice(0, 2);
    const tickets = [
      {
        role: 'outbound',
        Icon: ArrowRight,
        label: t('bundle.outbound'),
        departure: bundle?.outbound_departure_time,
        from: bundle?.outbound_start_city,
        to:   bundle?.outbound_end_city,
        feePct: bundle?.outbound_breakdown?.service_fee_percent
              ?? bundle?.outbound_service_fee_percent,
        km:     bundle?.outbound_breakdown?.distance_km
              ?? bundle?.outbound_distance_km,
        code:   bundle?.outbound_code,
      },
      {
        role: 'return',
        Icon: ArrowLeft,
        label: t('bundle.return'),
        departure: bundle?.return_departure_time,
        from: bundle?.return_start_city,
        to:   bundle?.return_end_city,
        feePct: bundle?.return_breakdown?.service_fee_percent
              ?? bundle?.return_service_fee_percent,
        km:     bundle?.return_breakdown?.distance_km
              ?? bundle?.return_distance_km,
        code:   bundle?.return_code,
      },
    ];
    const totalFee = bundle?.total_service_fee;
    const totalPrice = bundle?.total_price;
    const savingsPct = bundle?.savings_pct ?? 0;

    return (
      <div data-testid="bundle-success-container"
           style={{ width: '100%', textAlign: 'left' }}>
        {/* Compact header — pulse + title + savings on a single row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          marginBottom: 14, justifyContent: 'center', textAlign: 'center',
          flexWrap: 'wrap',
        }}>
          <div data-testid="bundle-success-pulse" style={{
            position: 'relative',
            width: 48, height: 48, borderRadius: 999,
            background: 'linear-gradient(135deg,#22c55e,#10b981)',
            color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 0 0 rgba(34,197,94,0.55)',
            animation: 'pulse-success 1.6s ease-out infinite',
            flex: '0 0 auto',
          }}>
            <Repeat size={22} />
            <Sparkles size={12} color="#fde047" style={{
              position: 'absolute', top: -3, right: -3,
            }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 data-testid="bundle-success-title" style={{
              margin: 0, fontSize: 18, fontWeight: 800, lineHeight: 1.2,
              background: 'linear-gradient(135deg,#7c3aed,#ec4899)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              {t('bundle.successTitle')}
            </h2>
            {savingsPct > 0 && (
              <div data-testid="bundle-success-savings" style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                color: '#15803d', fontWeight: 700, fontSize: 12, marginTop: 2,
              }}>
                <TrendingDown size={12} />
                {t('bundle.savingsHint', { pct: savingsPct })}
              </div>
            )}
          </div>
        </div>

        {/* TWO TICKETS side-by-side — both fit above the fold (~ < 600px tall) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 10, marginBottom: 12,
        }}>
          {tickets.map(({ role, Icon, label, departure, from, to, feePct, km, code }) => (
            <div
              key={role}
              data-testid={`bundle-ticket-${role}`}
              style={{
                padding: 12, borderRadius: 12,
                background: 'linear-gradient(135deg,#faf5ff,#fdf4ff)',
                border: '1.5px solid #c4b5fd',
                boxShadow: '0 4px 10px rgba(167,139,250,0.18)',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}
            >
              {/* Leg label */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 10, fontWeight: 800, letterSpacing: 0.4,
                padding: '3px 8px', borderRadius: 999,
                background: '#fff', color: '#7c3aed',
                border: '1px solid #e9d5ff', alignSelf: 'flex-start',
                textTransform: 'uppercase',
              }}>
                <Icon size={11} /> {label}
              </div>

              {/* Departure time — biggest visual element of each ticket */}
              <div data-testid={`bundle-ticket-${role}-departure`} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                color: '#0f172a', fontWeight: 800, fontSize: 14,
                lineHeight: 1.2,
              }}>
                <Clock size={14} color="#7c3aed" />
                {formatDeparture(departure, locale)}
              </div>

              {/* Route from → to */}
              {(from || to) && (
                <div style={{
                  fontSize: 12, color: '#475569', fontWeight: 600,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {from || '—'} <span style={{ color: '#94a3b8' }}>→</span> {to || '—'}
                </div>
              )}

              {/* Code + fee on a single row, monospace */}
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', gap: 8, marginTop: 2, fontSize: 11,
              }}>
                {code && (
                  <span data-testid={`bundle-ticket-${role}-code`} style={{
                    fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
                    background: '#0f172a', color: '#f1f5f9',
                    padding: '3px 8px', borderRadius: 6,
                    fontWeight: 700, letterSpacing: 1,
                  }}>
                    {t('bundle.code')} {code}
                  </span>
                )}
                <span style={{ color: '#64748b' }}>
                  {Number(km || 0).toFixed(0)} km · {(Number(feePct || 0) * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Single-row totals — fits without scroll */}
        <div data-testid="bundle-success-totals" style={{
          padding: '8px 12px', borderRadius: 10,
          background: 'linear-gradient(135deg,#7c3aed,#ec4899)',
          color: 'white', fontWeight: 800, fontSize: 13,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 10,
        }}>
          <span style={{ opacity: 0.9, fontSize: 11, fontWeight: 600 }}>
            {t('bundle.totalFee')}: {Number(totalFee || 0).toFixed(2)} €
          </span>
          <span>{t('bundle.totalPrice')} {Number(totalPrice || 0).toFixed(2)} €</span>
        </div>

        <p style={{
          fontSize: 11, color: '#6b7280', marginTop: 10, marginBottom: 0,
          textAlign: 'center',
        }}>
          {t('bookingSuccess.redirecting')}
        </p>

        <style>{`
          @keyframes pulse-success {
            0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.55); }
            70%  { box-shadow: 0 0 0 10px rgba(34,197,94,0);   }
            100% { box-shadow: 0 0 0 0 rgba(34,197,94,0);     }
          }
        `}</style>
      </div>
    );
  };

  return (
    <div className="auth-container" data-testid="booking-success-page">
      <div className="auth-card" style={{
        textAlign: 'center', maxWidth: 540,
        padding: bundleQuery ? '20px 22px' : undefined,
      }}>
        {status === 'checking' && (
          <>
            <Loader2 className="spinner" size={56} style={{ margin: '0 auto 1rem', animation: 'spin 1s linear infinite' }} />
            <h2 data-testid="payment-checking">{t('bookingSuccess.checking')}</h2>
            <p>{t('bookingSuccess.attempt', { current: attempts + 1, total: 5 })}</p>
          </>
        )}

        {status === 'success' && bundleQuery && renderBundleSuccess()}
        {status === 'success' && !bundleQuery && (
          <>
            <CheckCircle size={64} style={{ color: '#22c55e', margin: '0 auto 1rem' }} />
            <h2 data-testid="payment-success">{t('bookingSuccess.success')}</h2>
            <p>{t('bookingSuccess.confirmed')}</p>
            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '1rem' }}>
              {t('bookingSuccess.redirecting')}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle size={64} style={{ color: '#ef4444', margin: '0 auto 1rem' }} />
            <h2 data-testid="payment-error">{t('bookingSuccess.error')}</h2>
            <p>{t('bookingSuccess.contactSupport')}</p>
            <button onClick={() => navigate('/my-rides')} className="auth-button"
                    style={{ marginTop: '1rem' }} data-testid="go-to-rides-btn">
              {t('bookingSuccess.goToRides')}
            </button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <XCircle size={64} style={{ color: '#f59e0b', margin: '0 auto 1rem' }} />
            <h2 data-testid="payment-timeout">{t('bookingSuccess.timeout')}</h2>
            <p>{t('bookingSuccess.timeoutMessage')}</p>
            <button onClick={() => navigate('/my-rides')} className="auth-button"
                    style={{ marginTop: '1rem' }} data-testid="go-to-rides-timeout-btn">
              {t('bookingSuccess.goToRides')}
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <XCircle size={64} style={{ color: '#ef4444', margin: '0 auto 1rem' }} />
            <h2 data-testid="payment-expired">{t('bookingSuccess.expired')}</h2>
            <p>{t('bookingSuccess.tryAgain')}</p>
            <button onClick={() => navigate('/search')} className="auth-button"
                    style={{ marginTop: '1rem' }} data-testid="new-search-btn">
              {t('bookingSuccess.newSearch')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
